import { useEffect, useState } from "react";
import { Gift, Tag } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { money } from "../lib/helpers";
import { Input } from "./ui/input";
import { Button } from "./ui/button";

function Row({ label, value, testId, strong }) {
  return <div className={`flex justify-between gap-3 ${strong ? "font-semibold text-base" : ""}`} data-testid={testId}><span>{label}</span><span>{value}</span></div>;
}

function Breakdown({ q, lang, totalLabel }) {
  const m = (v) => money(v, q.currency, lang);
  return (
    <div className="rounded-[var(--radius)] border bg-muted/40 p-4 text-sm space-y-1" data-testid="pay-breakdown">
      {q.subtotal !== undefined && <Row label="Products" value={m(q.subtotal)} testId="pay-subtotal" />}
      {q.discount > 0 && <Row label={`Discount ${q.discount_code}`} value={`− ${m(q.discount)}`} testId="pay-discount" />}
      {q.shipping > 0 && <Row label="Delivery" value={m(q.shipping)} testId="pay-shipping" />}
      <Row label={totalLabel} value={m(q.total)} testId="pay-total" />
      {q.gift_applied > 0 && <Row label={`Gift card ${q.gift_code}`} value={`− ${m(q.gift_applied)}`} testId="pay-gift" />}
      <Row strong label="To pay now by card" value={m(q.charge_now)} testId="pay-charge-now" />
      {q.pay_later > 0 && <Row label="To pay at the clinic" value={m(q.pay_later)} testId="pay-later" />}
      {q.gift_code && <p className="text-xs text-muted-foreground pt-1" data-testid="pay-gift-left">Gift card balance after this booking: {m(q.gift_balance - q.gift_applied)}</p>}
    </div>
  );
}

function PayChoice({ q, full, onPick, lang }) {
  const opts = [[false, q.deposit > 0 ? `Pay deposit only (${money(q.deposit, q.currency, lang)})` : "Pay at the clinic"], [true, "Pay in full now"]];
  return (
    <div className="grid sm:grid-cols-2 gap-2" data-testid="pay-choice">{opts.map(([v, label]) => (
      <button key={String(v)} type="button" data-testid={`pay-choice-${v ? "full" : "deposit"}`} onClick={() => onPick(v)}
        className={`rounded-[var(--radius)] border px-4 py-3 text-sm text-left transition-colors ${full === v ? "border-primary ring-2 ring-primary bg-primary/5" : "bg-card hover:border-primary"}`}>{label}</button>))}
    </div>
  );
}

export function BookingPay({ slug, base, email, value, onChange, onQuote, allowDeposit = true, lang, quoteUrl, totalLabel = "Treatment total" }) {
  const [codes, setCodes] = useState({ code: value.code, gift_code: value.gift_code });
  const [q, setQ] = useState(null);
  const [err, setErr] = useState("");
  const key = JSON.stringify(base);
  useEffect(() => {
    api.post(quoteUrl || `/public/c/${slug}/quote`, { ...base, email, ...value })
      .then((r) => { setQ(r.data); setErr(""); onQuote(r.data); })
      .catch((e) => { setErr(errMsg(e)); onQuote(null); });
  }, [slug, key, value.code, value.gift_code, value.pay_full]); // eslint-disable-line react-hooks/exhaustive-deps
  const apply = () => onChange({ ...value, code: codes.code.trim(), gift_code: codes.gift_code.trim() });
  const changed = codes.code.trim() !== value.code || codes.gift_code.trim() !== value.gift_code;
  return (
    <div className="space-y-3" data-testid="booking-pay">
      <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-2 items-end">
        <label className="text-xs text-muted-foreground space-y-1"><span className="flex items-center gap-1"><Tag className="h-3.5 w-3.5" />Discount code</span>
          <Input data-testid="book-code-input" className="uppercase" placeholder="e.g. BDAY-… or NEWS10" value={codes.code} onChange={(e) => setCodes({ ...codes, code: e.target.value })} /></label>
        <label className="text-xs text-muted-foreground space-y-1"><span className="flex items-center gap-1"><Gift className="h-3.5 w-3.5" />Gift card code</span>
          <Input data-testid="book-gift-input" className="uppercase" placeholder="GIFT-…" value={codes.gift_code} onChange={(e) => setCodes({ ...codes, gift_code: e.target.value })} /></label>
        <Button type="button" variant={changed ? "default" : "outline"} className="rounded-full" data-testid="book-apply-codes" onClick={apply}>Apply</Button>
      </div>
      {err && <p className="text-sm text-destructive" data-testid="pay-error">{err}</p>}
      {q && allowDeposit && q.total > 0 && <PayChoice q={q} full={value.pay_full} lang={lang} onPick={(v) => onChange({ ...value, pay_full: v })} />}
      {q && q.gift_code && q.pay_now === 0 && <p className="text-xs text-primary" data-testid="pay-gift-hint">Choose “Pay in full now” to use your gift card balance online.</p>}
      {q && <Breakdown q={q} lang={lang} totalLabel={totalLabel} />}
    </div>
  );
}
